import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Building2, CheckCircle, AlertTriangle, XCircle, UserPlus } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { StatusBadge } from '../components/OrgCard'
import AddTrialOrgModal from '../components/AddTrialOrgModal'
import OpsMobileNav from '../components/OpsMobileNav'

interface OrgRow {
  id: string; name: string; email: string; status: string
  plan_name: string; plan_code: string; billing_cycle: string
  expires_at: string; user_count: number; days_left: number | null
}

type FilterTab = 'all' | 'active' | 'expiring' | 'expired'

function daysUntil(dateStr: string): number | null {
  if (!dateStr) return null
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function ExpiryBadge({ days }: { days: number | null }) {
  if (days === null) return <span className="text-xs" style={{ color: '#6b7280' }}>—</span>

  let color: string, bg: string, label: string
  if (days < 0) {
    color = '#ef4444'; bg = '#ef444420'; label = `Expired ${Math.abs(days)}d ago`
  } else if (days <= 7) {
    color = '#ef4444'; bg = '#ef444420'; label = `${days}d left`
  } else if (days <= 30) {
    color = '#f59e0b'; bg = '#f59e0b20'; label = `${days}d left`
  } else {
    color = '#22c55e'; bg = '#22c55e20'; label = `${days}d left`
  }

  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ color, background: bg }}>
      {label}
    </span>
  )
}

function BillingBadge({ cycle }: { cycle: string }) {
  if (!cycle || cycle === '—') return null
  const isAnnual = cycle === 'yearly' || cycle === 'annual'
  return (
    <span className="text-xs px-1.5 py-0.5 rounded" style={{
      color: isAnnual ? '#a78bfa' : '#9ca3af',
      background: isAnnual ? '#7c3aed15' : '#9ca3af15',
    }}>
      {isAnnual ? 'Annual' : 'Monthly'}
    </span>
  )
}

const FILTER_TABS: { key: FilterTab; label: string; icon: typeof Building2; color: string }[] = [
  { key: 'all', label: 'All', icon: Building2, color: '#6366f1' },
  { key: 'active', label: 'Active', icon: CheckCircle, color: '#22c55e' },
  { key: 'expiring', label: 'Expiring Soon', icon: AlertTriangle, color: '#f59e0b' },
  { key: 'expired', label: 'Expired', icon: XCircle, color: '#ef4444' },
]

export default function OrganizationsPage() {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<OrgRow[]>([])
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<FilterTab>('all')
  const [loading, setLoading] = useState(true)
  const [showTrialModal, setShowTrialModal] = useState(false)

  const loadOrgs = async () => {
    const { data } = await supabase
      .from('organizations')
      .select(`id, name, email, status, org_subscriptions(status, expires_at, billing_cycle, subscription_plans(name, plan_code)), org_users(user_id)`)
      .order('created_at', { ascending: false })
    const rows = (data ?? []).map((o: any) => {
      const sub = Array.isArray(o.org_subscriptions) ? o.org_subscriptions[0] : o.org_subscriptions
      const expiresAt = sub?.expires_at ?? ''
      return {
        id: o.id, name: o.name, email: o.email ?? '—',
        status: sub?.status ?? o.status ?? '—',
        plan_name: sub?.subscription_plans?.name ?? '—',
        plan_code: sub?.subscription_plans?.plan_code ?? '',
        billing_cycle: sub?.billing_cycle ?? '—',
        expires_at: expiresAt,
        user_count: Array.isArray(o.org_users) ? o.org_users.length : 0,
        days_left: daysUntil(expiresAt),
      }
    })
    setOrgs(rows)
    setLoading(false)
  }

  useEffect(() => { loadOrgs() }, [])

  const filtered = orgs.filter(o => {
    // Tab filter
    if (tab === 'active' && (o.days_left === null || o.days_left < 0)) return false
    if (tab === 'expiring' && (o.days_left === null || o.days_left < 0 || o.days_left > 30)) return false
    if (tab === 'expired' && (o.days_left === null || o.days_left >= 0)) return false
    // Search
    if (search) {
      const q = search.toLowerCase()
      return o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q) || o.plan_name.toLowerCase().includes(q)
    }
    return true
  })

  const counts = {
    all: orgs.length,
    active: orgs.filter(o => o.days_left !== null && o.days_left >= 0).length,
    expiring: orgs.filter(o => o.days_left !== null && o.days_left >= 0 && o.days_left <= 30).length,
    expired: orgs.filter(o => o.days_left !== null && o.days_left < 0).length,
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
    </div>
  )

  return (
    <div className="space-y-5">
      <OpsMobileNav />
      {/* Header + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Organizations</h1>
          <p className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>{orgs.length} total</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button onClick={() => setShowTrialModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors whitespace-nowrap"
            style={{ background: '#6366f1' }}>
            <UserPlus size={15} /> Add Trial
          </button>
          <div className="relative flex-1 sm:flex-none">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#6b7280' }} />
            <input
              type="text" placeholder="Search…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
              style={{ background: '#252836', border: '1px solid #3f4258' }}
            />
          </div>
        </div>
      </div>

      {/* Add Trial Org Modal */}
      <AddTrialOrgModal
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        onSuccess={(orgId) => {
          setShowTrialModal(false)
          loadOrgs()
          navigate(`/organizations/${orgId}`)
        }}
      />

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {FILTER_TABS.map(t => {
          const active = tab === t.key
          const Icon = t.icon
          return (
            <button key={t.key} onClick={() => setTab(active ? 'all' : t.key)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: active ? `${t.color}20` : '#252836',
                color: active ? t.color : '#9ca3af',
                border: `1px solid ${active ? t.color + '40' : '#2d3148'}`,
              }}>
              <Icon size={14} />
              {t.label}
              <span className="text-xs opacity-70">({counts[t.key]})</span>
            </button>
          )
        })}
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: '#252836', border: '1px solid #2d3148' }}>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid #2d3148' }}>
                {['Name', 'Email', 'Status', 'Plan', 'Billing', 'Subscription', 'Users'].map(h => (
                  <th key={h} className="text-center px-5 py-3 font-medium" style={{ color: '#9ca3af' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center" style={{ color: '#6b7280' }}>No organizations found</td></tr>
              ) : filtered.map((org, i) => (
                <tr
                  key={org.id}
                  className="cursor-pointer transition-colors hover:bg-white/5"
                  style={{ borderBottom: i < filtered.length - 1 ? '1px solid #2d3148' : undefined }}
                  onClick={() => navigate(`/organizations/${org.id}`)}
                >
                  <td className="px-5 py-3 text-white font-medium">{org.name}</td>
                  <td className="px-5 py-3 text-sm" style={{ color: '#9ca3af' }}>{org.email}</td>
                  <td className="px-5 py-3"><StatusBadge status={org.status} /></td>
                  <td className="px-5 py-3">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>{org.plan_name}</span>
                  </td>
                  <td className="px-5 py-3"><BillingBadge cycle={org.billing_cycle} /></td>
                  <td className="px-5 py-3"><ExpiryBadge days={org.days_left} /></td>
                  <td className="px-5 py-3 text-sm" style={{ color: '#9ca3af' }}>{org.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y" style={{ borderColor: '#2d3148' }}>
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: '#6b7280' }}>No organizations found</div>
          ) : filtered.map(org => (
            <div
              key={org.id}
              className="px-4 py-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => navigate(`/organizations/${org.id}`)}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{org.name}</div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: '#9ca3af' }}>{org.email}</div>
                </div>
                <StatusBadge status={org.status} />
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: '#9ca3af' }}>
                <span className="px-2 py-0.5 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>{org.plan_name}</span>
                <BillingBadge cycle={org.billing_cycle} />
                <ExpiryBadge days={org.days_left} />
                <span>{org.user_count} users</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
